/*
 *  KAN_Avatar.js
 *  Project: Kanoogi Library System
 *
 *  Created by Chris Taylor on 05/30/26
 *  Copyright © 2026 Kanoogi Inc. | All Rights Reserved
 *
 */

"use strict";

// Deterministic default-avatar generator. When a user has no custom profile pic, every display
// surface (nav chip, profile previews, forum/chat comments) falls back to one of these: a solid
// colored square with the user's initials. Square on purpose - each surface rounds the corners
// however it likes (4px nav, 20% squircle in forums), matching the real-pic convention.
//
// Output is an inline SVG data URL: crisp at any size, no canvas, no network. Color is hashed from
// a stable seed (userid) so a given user always gets the same avatar everywhere.

/* ---------------------------------------------------------------- */

// HashCode() - Tiny deterministic string hash (djb2-ish). Stable across browsers/sessions.

function HashCode(str)
	{
	let h=5381;
	const s=String(str==null?'':str);
	for (let i=0; i<s.length; i++)
		h=((h<<5)+h+s.charCodeAt(i))|0;								// h*33 + c, kept in 32-bit
	return(Math.abs(h));
	}
/* ---------------------------------------------------------------- */

// Initials() - Up to two uppercase alphanumeric initials from a display name. Sanitizes to
// [A-Z0-9] only, which also neutralizes any injection risk when interpolated into the SVG.

export function Initials(label)
	{
	const clean=String(label==null?'':label).trim();
	if (!clean) return('?');

	const words=clean.split(/\s+/).filter(Boolean);
	let raw;
	if (words.length>=2)
		raw=words[0].charAt(0)+words[1].charAt(0);					// First letter of first two words
	else
		raw=clean.slice(0,2);										// Single word -> first two chars

	const initials=raw.toUpperCase().replace(/[^A-Z0-9]/g,'');
	return(initials||'?');
	}
/* ---------------------------------------------------------------- */

// AvatarDataURL() - A deterministic SVG default-avatar data URL. `seed` drives the color (use a
// stable id like userid); `label` drives the initials (the display name). size is the rendered
// px square (the SVG scales, so it's mostly a hint for raster consumers).

export function AvatarDataURL(seed, label, size)
	{
	const px=size||400;
	const initials=Initials(label);

	// Hue from the seed; fixed saturation/lightness tuned for white-text contrast. Falling back to
	// the label as the seed keeps anonymous-but-named cases stable too.
	const hue=HashCode((seed==null||seed==='')?label:seed)%360;
	const bg=`hsl(${hue},45%,42%)`;
	const fontSize=initials.length>1?44:54;							// One big letter vs two smaller

	// Double-quoted SVG attributes on purpose: encodeURIComponent turns " into %22 but leaves '
	// untouched, so a double-quoted SVG yields a quote-free data URL that's safe to drop into either
	// a single- or double-quoted HTML attribute (the forum/chat renderers embed it in onerror).
	const svg=
		`<svg xmlns="http://www.w3.org/2000/svg" width="${px}" height="${px}" viewBox="0 0 100 100">`+
		`<rect width="100" height="100" fill="${bg}"/>`+
		`<text x="50" y="50" dy=".02em" fill="#ffffff" font-family="Trebuchet MS, Verdana, sans-serif" `+
		`font-size="${fontSize}" font-weight="600" text-anchor="middle" dominant-baseline="central">${initials}</text>`+
		`</svg>`;

	return('data:image/svg+xml,'+encodeURIComponent(svg));
	}
/* ---------------------------------------------------------------- */
